// Holoscreen — associate-facing, set-and-forget rate board for a thin client.
//
// Mirrors the EWOK Holoscreen model: a dedicated full-screen view that renders
// OUTSIDE the Cognito AuthGate (see index.js) so a signage thin client can load
// it with no interactive login. Per builder-security, this view is served to a
// different, device-authorized audience (FC floor display via the EWOK-style
// ServiceProxy/signage path) — it does NOT weaken the operator console's gate,
// and it reads only a narrow, non-sensitive slice (OLM1 EOL/AROS quarter
// rate + carton numbers). No associate PII is shown.
//
// URL: ...?holoscreen=1&site=OLM1   (site defaults to OLM1)
//
// Layout (from the requested mock): rows Q1..Q4, each with two sub-rows
// Rate (CPH) and Cartons; columns EOL and AROS.
import React, { useEffect, useState, useCallback } from 'react';

const API = 'https://22tp4u1f2h.execute-api.us-west-2.amazonaws.com/prod';

// Shift-quarter hour buckets (same boundaries as the IDRT quarterly view).
const QUARTERS = {
  day:   { q1: ['8', '9'], q2: ['10', '11', '12'], q3: ['13', '14', '15'], q4: ['16', '17', '18'] },
  night: { q1: ['19', '20'], q2: ['21', '22', '23'], q3: ['0', '1', '2'], q4: ['3', '4'] },
};
// MP_WINDOW carton windows map to quarter ends (cumulative), so quarter cartons
// are successive differences of the 0600/1200/1830/full cumulative points.
// For a first pass we show the cumulative MP AROS cartons per window and derive
// EOL from the IDRT EOL jobs; see computeCartons below.

function currentShift() {
  const h = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })).getHours();
  return (h >= 7 && h < 19) ? 'day' : 'night';
}
function currentDatePT() {
  const pac = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  if (pac.getHours() < 7) pac.setDate(pac.getDate() - 1);
  return `${pac.getFullYear()}-${String(pac.getMonth() + 1).padStart(2, '0')}-${String(pac.getDate()).padStart(2, '0')}`;
}

export default function Holoscreen() {
  const params = new URLSearchParams(window.location.search);
  const site = (params.get('site') || 'OLM1').toUpperCase();

  const [date] = useState(currentDatePT());
  const [shift] = useState(currentShift());
  const [eol, setEol] = useState(null);   // EOL_SCANS rows (IDRT per-AA, has byHour)
  const [updated, setUpdated] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/api/fclm/cache?site=${site}&date=EOL_SCANS_${date}&shift_type=${shift}`)
      .then((r) => r.json())
      .then((d) => { setEol(d && Array.isArray(d.rows) ? d : { rows: [] }); setUpdated(new Date()); })
      .catch(() => setEol({ rows: [] }));
  }, [site, date, shift]);

  // Set-and-forget: auto-refresh every 60s; no user interaction needed.
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  // Per-quarter IDRT rate (CPH) + jobs from the byHour buckets.
  // v1: the eol-scans feed does not yet tag EOL vs AROS, so we show the COMBINED
  // IDRT rate for all associates. The EOL/AROS split (target state) is a backend
  // sync change tracked as a fast follow — the column structure below is kept
  // ready for it. "Cartons" currently shows IDRT jobs; real EOL/AROS carton
  // sourcing (PID×EOL% + MP_WINDOW AROS) lands with the split.
  const quarterData = (q) => {
    const hrs = (QUARTERS[shift] || QUARTERS.day)[q] || [];
    let h = 0, j = 0;
    (eol?.rows || []).forEach((r) => {
      const bh = r.byHour || {};
      hrs.forEach((H) => { if (bh[H]) { h += bh[H].h || 0; j += bh[H].j || 0; } });
    });
    return { rate: h > 0 ? Math.round((j / h) * 10) / 10 : null, cartons: Math.round(j) || null };
  };

  const quarters = ['q1', 'q2', 'q3', 'q4'];

  // Big-screen styling (dark, high-contrast, readable from across the floor).
  const wrap = { background: '#111', color: '#eee', minHeight: '100vh', margin: 0, padding: '2vh 3vw', fontFamily: 'Arial, sans-serif', boxSizing: 'border-box' };
  const h1 = { fontSize: '4vw', fontWeight: 800, margin: '0 0 2vh', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' };
  const table = { width: '100%', borderCollapse: 'collapse', fontSize: '3vw' };
  const th = { border: '1px solid #444', padding: '1.2vh 1vw', textAlign: 'center', background: '#1e2a38', fontWeight: 700 };
  const qcell = { border: '1px solid #444', padding: '1.2vh 1vw', fontWeight: 800, background: '#1e2a38', textAlign: 'center' };
  const lbl = { border: '1px solid #444', padding: '1.2vh 1vw', color: '#9bb', textAlign: 'left' };
  const val = { border: '1px solid #444', padding: '1.2vh 1vw', textAlign: 'center', fontWeight: 700 };
  const rateColor = (r) => (r == null ? '#666' : r >= 135 ? '#2ecc71' : '#e74c3c');

  return (
    <div style={wrap}>
      <div style={h1}>
        <span>{site} — IDRT Rates</span>
        <span style={{ fontSize: '2vw', color: '#9bb' }}>{shift === 'day' ? 'Day' : 'Night'} · {date}</span>
      </div>
      <table style={table}>
        <thead>
          <tr>
            <th style={th}></th><th style={th}></th>
            <th style={th}>IDRT (EOL + AROS)</th>
          </tr>
        </thead>
        <tbody>
          {quarters.map((q) => {
            const d = quarterData(q);
            return (
              <React.Fragment key={q}>
                <tr>
                  <td style={{ ...qcell, borderBottom: 'none' }} rowSpan={2}>{q.toUpperCase()}</td>
                  <td style={lbl}>Rate (CPH)</td>
                  <td style={{ ...val, color: rateColor(d.rate) }}>{d.rate == null ? '—' : d.rate.toFixed(1)}</td>
                </tr>
                <tr>
                  <td style={lbl}>Cartons</td>
                  <td style={{ ...val, color: '#ddd' }}>{d.cartons == null ? '—' : d.cartons.toLocaleString()}</td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <div style={{ marginTop: '2vh', fontSize: '1.3vw', color: '#667' }}>
        Updated {updated ? updated.toLocaleTimeString('en-US', { timeZone: 'America/Los_Angeles', hour: 'numeric', minute: '2-digit' }) : '—'} · auto-refresh 60s · EOL/AROS split coming
      </div>
    </div>
  );
}
